import { GridRowId } from '@mui/x-data-grid-pro';
import { TsosStateProps } from '@pages/MeasurementVisualization/utils';
import { ApiApplicationServicesDataTypesTrendTypesTrendResultsCount } from '@redux/apiSlices/trendApi';
import { RootState } from '@redux/store';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export type SelectedDevicesByTso = { [key: string]: string[] | undefined };

export type ActiveTrendParametersProps = {
  activeTsos: TsosStateProps[];
  startDate: string;
  endDate: string;
  measurmentParameters: string[] | [];
  measurmentDevices: SelectedDevicesByTso;
};

export type SelectedCell = {
  id: GridRowId;
  field: string;
  measurementDeviceId: number;
};

export type ActiveTrendProps = {
  disabled: boolean;
  id: number;
  label: string;
  value: string;
  waiting: boolean;
};

export type DeviceDetails = {
  tsoId: number;
  measurementDeviceName: 'string';
  measurementPointName: 'string';
  substationName: 'string';
};

export type TrendIdentifiactionState = {
  activeTrendParameters: ActiveTrendParametersProps;
  activeTrend: ActiveTrendProps;
  trendName: string;
  isLoading: boolean;
  isFetchingTredData: boolean;
  isFiltering: boolean;
  isFetchingChartData: boolean;
  trendData: ApiApplicationServicesDataTypesTrendTypesTrendResultsCount[] | [];
  selectedDeviceDetails: { [key: string]: DeviceDetails };
  selectedCells: SelectedCell[];
  trendChartsData: TrendChartsDataMap;
  didUserApplyFilters: boolean;
};

export type TrendValue = {
  weekStart: string;
  value: number;
};

export type TrendChartEntry = {
  measurementParameterId: number;
  trendValues: TrendValue[];
};

export type TrendChartsDataMap = {
  [deviceId: number]: {
    [parameterId: number]: TrendChartEntry;
  };
};

const activeTrendParameters: ActiveTrendParametersProps = {
  activeTsos: [],
  startDate: '',
  endDate: '',
  measurmentParameters: [],
  measurmentDevices: {},
};

const activeTrend: ActiveTrendProps = {
  disabled: false,
  id: Number.NaN,
  label: '',
  value: '',
  waiting: false,
};

const initialState: TrendIdentifiactionState = {
  activeTrendParameters,
  activeTrend,
  trendName: '',
  isLoading: false,
  isFetchingTredData: false,
  isFiltering: false,
  isFetchingChartData: false,
  trendData: [],
  selectedDeviceDetails: {},
  selectedCells: [],
  trendChartsData: {},
  didUserApplyFilters: false,
};

// As per Sonar's complaints, this has to live here to avoid circular dependencies
export const normalizeCategoryPath = (categoryPath: string) => {
  return categoryPath.replace('.phasephase', '').replace('.phaseground', '');
};

const trendIdentificationSlice = createSlice({
  name: 'trendIdentifiaction',
  initialState,
  reducers: {
    setActiveTrendParameters: (state, action: PayloadAction<ActiveTrendParametersProps>) => {
      state.activeTrendParameters = action.payload;
    },
    setActiveTrendName: (state, action: PayloadAction<string>) => {
      state.trendName = action.payload;
    },
    setActiveTrend: (state, action: PayloadAction<ActiveTrendProps>) => {
      state.activeTrend = action.payload;
    },
    setIsLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    setIsFetchingTredData: (state, action: PayloadAction<boolean>) => {
      state.isFetchingTredData = action.payload;
    },
    setIsFiltering: (state, action: PayloadAction<boolean>) => {
      state.isFiltering = action.payload;
    },
    setTredData: (state, action: PayloadAction<ApiApplicationServicesDataTypesTrendTypesTrendResultsCount[] | []>) => {
      state.trendData = action.payload;
    },
    setSelectedDeviceDetails: (state, action: PayloadAction<{ [key: string]: DeviceDetails }>) => {
      state.selectedDeviceDetails = action.payload;
    },
    setSelectedCells: (state, action: PayloadAction<SelectedCell[]>) => {
      state.selectedCells = action.payload;
    },
    addTrendChartData: (
      state,
      action: PayloadAction<{
        measurementDeviceId: number;
        response: TrendChartEntry[];
      }>
    ) => {
      const { measurementDeviceId, response } = action.payload;

      if (!state.trendChartsData[measurementDeviceId]) {
        state.trendChartsData[measurementDeviceId] = {};
      }

      for (const entry of response) {
        state.trendChartsData[measurementDeviceId][entry.measurementParameterId] = entry;
      }
    },

    removeTrendChartData: (
      state,
      action: PayloadAction<{
        measurementDeviceId: number;
        categoryPath: string;
        allParameters: any[];
      }>
    ) => {
      const { measurementDeviceId, categoryPath, allParameters } = action.payload;

      const deviceCharts = state.trendChartsData[measurementDeviceId];
      if (!deviceCharts) return;

      const normalizedPath = normalizeCategoryPath(categoryPath);

      const idsToRemove: number[] = [];

      for (const entry of Object.values(deviceCharts)) {
        const param = allParameters.find((p) => p.id === entry.measurementParameterId);

        if (param && normalizeCategoryPath(param.categoryPath) === normalizedPath) {
          idsToRemove.push(entry.measurementParameterId);
        }
      }

      for (const id of idsToRemove) {
        delete deviceCharts[id];
      }

      if (Object.keys(deviceCharts).length === 0) {
        delete state.trendChartsData[measurementDeviceId];
      }
    },
    setDidUserApplyFilters: (state, action: PayloadAction<boolean>) => {
      state.didUserApplyFilters = action.payload;
    },
    clearAllTrendChartData: (state) => {
      state.trendChartsData = {};
    },
  },
});

export const selectAppliedParameters = (state: RootState) => state.trendIdentification.activeTrendParameters;
export const selectActiveTrend = (state: RootState) => state.trendIdentification.activeTrend;
export const selectIsLoading = (state: RootState) => state.trendIdentification.isLoading;
export const selectIsFetchingTredData = (state: RootState) => state.trendIdentification.isFetchingTredData;
export const selectIsFiltering = (state: RootState) => state.trendIdentification.isFiltering;
export const selectTrendData = (state: RootState) => state.trendIdentification.trendData;
export const selectSelectedDeviceDetails = (state: RootState) => state.trendIdentification.selectedDeviceDetails;
export const selectSelectedCells = (state: RootState) => state.trendIdentification.selectedCells;
export const selectTrendChartsData = (state: RootState) => state.trendIdentification.trendChartsData;
export const selectDidUserApplyFilters = (state: RootState) => state.trendIdentification.didUserApplyFilters;

export const {
  setActiveTrendParameters,
  setActiveTrendName,
  setActiveTrend,
  setIsLoading,
  setIsFetchingTredData,
  setTredData,
  setIsFiltering,
  setSelectedDeviceDetails,
  setSelectedCells,
  addTrendChartData,
  removeTrendChartData,
  setDidUserApplyFilters,
  clearAllTrendChartData,
} = trendIdentificationSlice.actions;

export default trendIdentificationSlice.reducer;
